import { makeAutoObservable, runInAction } from "mobx";
import { Contact, Activity, Account } from "types/common/ServiceStoreTypes";
import RootStore from "../../../../../stores/RootStore";
import ServiceStore from "../../../../../stores/ServiceStore";

export interface ContactFormData {
  // Basic Information
  firstName: string;
  lastName: string;
  jobTitle: string;
  department: string;
  email: string;
  phone: string;
  mobilePhone: string;

  // Account Association
  accountId: number | null;

  // Address
  mailingStreet: string;
  mailingCity: string;
  mailingState: string;
  mailingPostalCode: string;
  mailingCountry: string;

  // Professional Information
  linkedInProfile: string;

  // Status and Lifecycle
  status: string;
  isPrimaryContact: boolean;
}

export interface ContactFormValidation {
  firstName: string[];
  lastName: string[];
  email: string[];
  phone: string[];
  accountId: string[];
  mailingStreet: string[];
  mailingCity: string[];
  mailingState: string[];
  mailingPostalCode: string[];
  mailingCountry: string[];
  general: string[];
}

export class ContactFormStore {
  rootStore: RootStore;

  // Observable properties
  contact: Contact | null = null;
  contactId: number | null = null;
  isLoading = false;
  isLoadingAccounts = false;
  availableAccounts: Account[] = [];
  validation: ContactFormValidation = this.getInitialValidation();
  isDirty = false;
  isSubmitting = false;

  // Available data for form
  availableStatuses = ["Active", "Inactive", "Prospect", "Customer"];

  // Form data
  formData: ContactFormData = {
    firstName: "",
    lastName: "",
    jobTitle: "",
    department: "",
    email: "",
    phone: "",
    mobilePhone: "",
    accountId: null,
    linkedInProfile: "",
    isPrimaryContact: false,
    status: "Active",
    mailingStreet: "",
    mailingCity: "",
    mailingState: "",
    mailingPostalCode: "",
    mailingCountry: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Computed values
  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  get currentContact(): Contact | null {
    return this.contactId
      ? this.contactsStore.entities.find((c: Contact) => c.contactId === this.contactId)
      : null;
  }

  get contactsLoading(): boolean {
    return this.contactsStore.isServicePending;
  }

  get accountsLoading(): boolean {
    return this.customersStore.isServicePending;
  }

  // Initialization
  async initializeForm(contactId?: number) {
    this.isLoading = true;
    this.contactId = contactId || null;

    try {
      // Load accounts for dropdown
      await this.loadAvailableAccounts();

      if (contactId) {
        // Edit mode - load existing contact
        await this.loadContacts(contactId);
      } else {
        // Create mode - reset form
        this.resetForm();
      }
    } catch (error) {
      console.error("Failed to initialize contact form:", error);
      this.validation.general = [
        "Failed to load contact form. Please try again.",
      ];
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async loadContacts(contactId?: number) {
    try {
      const contactsStore = this.rootStore.contactsStore as ServiceStore;
      await contactsStore.find();

      // If we have a contactId, find and populate the form with that contact
      if (contactId) {
        const contact = contactsStore.entities.find((c: unknown) => {
          const contactData = c as { contactId: number };
          return contactData.contactId === contactId;
        });

        if (contact) {
          runInAction(() => {
            this.contact = contact as unknown as Contact;
            this.populateFormFromContact(contact as unknown as Contact);
          });
        } else {
          throw new Error("Contact not found");
        }
      }
    } catch (error) {
      console.error("Failed to load contacts:", error);
      this.validation.general = [
        "Failed to load contacts. Please check the contact ID.",
      ];
    }
  }

  async loadAvailableAccounts() {
    this.isLoadingAccounts = true;
    try {
      const customersStore = this.rootStore.customersStore as ServiceStore;
      await customersStore.find();
    } catch (error) {
      console.error("Failed to load accounts:", error);
    } finally {
      runInAction(() => {
        this.isLoadingAccounts = false;
      });
    }
  }

  // Form data management
  updateField = <K extends keyof ContactFormData>(
    field: K,
    value: ContactFormData[K]
  ) => {
    runInAction(() => {
      this.formData[field] = value;
      this.isDirty = true;

      // Clear validation errors for this field
      this.clearFieldValidation(field);
    });
  };

  updateMailingField = (
    field:
      | "mailingStreet"
      | "mailingCity"
      | "mailingState"
      | "mailingPostalCode"
      | "mailingCountry",
    value: string
  ) => {
    runInAction(() => {
      this.formData[field] = value;
      this.isDirty = true;

      // Clear address validation errors for this field
      if (this.validation[field]) {
        this.validation[field] = [];
      }
    });
  };

  // Form validation
  validateForm(): boolean {
    const validation = this.getInitialValidation();
    let isValid = true;
    console.log("validateForm called");
    console.log("Form data:", this.formData);

    // Required field validation
    if (!this.formData.firstName.trim()) {
      validation.firstName.push("First name is required");
      isValid = false;
    }

    if (!this.formData.lastName.trim()) {
      validation.lastName.push("Last name is required");
      isValid = false;
    }

    if (!this.formData.email.trim()) {
      validation.email.push("Email is required");
      isValid = false;
    } else if (!this.isValidEmail(this.formData.email)) {
      validation.email.push("Please enter a valid email address");
      isValid = false;
    }

    if (!this.formData.accountId) {
      validation.accountId.push("Account selection is required");
      isValid = false;
    }

    // Phone validation (if provided)
    if (this.formData.phone && !this.isValidPhone(this.formData.phone)) {
      validation.phone.push("Please enter a valid phone number");
      isValid = false;
    }

    // Address validation (basic)
    if (this.formData.mailingStreet && !this.formData.mailingCity) {
      validation.mailingCity.push(
        "City is required when street address is provided"
      );
      isValid = false;
    }

    if (this.formData.mailingStreet && !this.formData.mailingCountry) {
      validation.mailingCountry.push(
        "Country is required when street address is provided"
      );
      isValid = false;
    }

    runInAction(() => {
      this.validation = validation;
    });

    return isValid;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPhone(phone: string): boolean {
    // Basic phone validation - can be enhanced based on requirements
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ""));
  }

  clearFieldValidation(field: keyof ContactFormData) {
    if (field in this.validation) {
      runInAction(() => {
        const validationKey = field as keyof ContactFormValidation;
        this.validation[validationKey] = [];
      });
    }
  }

  // Form submission
  async saveContact(): Promise<boolean> {
    if (this.isSubmitting) return false;

    runInAction(() => {
      this.isSubmitting = true;
      this.validation.general = [];
    });

    try {
      // Validate form
      if (!this.validateForm()) {
        return false;
      }

      const contactData: Partial<Contact> = this.buildContactFromForm();

      let savedContact: Contact;
      if (this.contactId) {
        // Update existing contact
        console.log("Updating contact:", this.contactId, contactData);
        const contactsStore = this.rootStore.contactsStore as ServiceStore;
        savedContact = (await contactsStore.patch(
          this.contactId,
          contactData
        )) as unknown as Contact;
      } else {
        // Create new contact
        console.log("Creating contact:", contactData);
        const contactsStore = this.rootStore.contactsStore as ServiceStore;
        savedContact = (await contactsStore.create(
          contactData
        )) as unknown as Contact;
      }

      // Log activity
      await this.logContactActivity(
        savedContact,
        this.contactId ? "updated" : "created"
      );

      runInAction(() => {
        this.contact = savedContact;
        this.contactId = savedContact.contactId;
        this.isDirty = false;
      });

      return true;
    } catch (error) {
      console.error("Failed to save contact:", error);
      runInAction(() => {
        this.validation.general = ["Failed to save contact. Please try again."];
      });
      return false;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  private buildContactFromForm(): Partial<Contact> {
    return {
      firstName: this.formData.firstName.trim(),
      lastName: this.formData.lastName.trim(),
      jobTitle: this.formData.jobTitle.trim(),
      department: this.formData.department.trim(),
      email: this.formData.email.trim(),
      phone: this.formData.phone.trim(),
      phoneNumber: this.formData.phone.trim(), // Use Phone field for PhoneNumber
      mobilePhone: this.formData.mobilePhone.trim(),
      accountId: this.formData.accountId!,
      mailingStreet: this.formData.mailingStreet?.trim(),
      mailingCity: this.formData.mailingCity?.trim(),
      mailingState: this.formData.mailingState?.trim(),
      mailingPostalCode: this.formData.mailingPostalCode?.trim(),
      mailingCountry: this.formData.mailingCountry?.trim(),
      linkedInProfile: this.formData.linkedInProfile?.trim(),
      isPrimaryContact: this.formData.isPrimaryContact,
      status: this.formData.status,
      updatedAt: new Date().toISOString(),
    };
  }

  private async logContactActivity(
    contact: Contact,
    action: "created" | "updated"
  ) {
    try {
      const activity: Omit<Activity, "activityId"> = {
        activityType: "contact",
        subject: `Contact ${action}`,
        description: `Contact ${action} via form`,
        status: "Completed",
        priority: "Low",
        followUpRequired: false,
        contactId: contact.contactId,
        accountId: contact.accountId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 1, // Replace with actual user ID
      };

      console.log("Logging activity:", activity);
      // await this.rootStore.activitiesStore.create(activity);
    } catch (error) {
      console.error("Failed to log contact activity:", error);
      // Don't fail the save operation for activity logging failure
    }
  }

  // Utility methods
  private populateFormFromContact(contact: Contact) {
    this.formData = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      jobTitle: contact.jobTitle || "",
      department: contact.department || "",
      email: contact.email || "",
      phone: contact.phone || "",
      mobilePhone: contact.mobilePhone || "",
      accountId: contact.accountId || null,
      mailingStreet: contact.mailingStreet || "",
      mailingCity: contact.mailingCity || "",
      mailingState: contact.mailingState || "",
      mailingPostalCode: contact.mailingPostalCode || "",
      mailingCountry: contact.mailingCountry || "",
      linkedInProfile: contact.linkedInProfile || "",
      isPrimaryContact: contact.isPrimaryContact,
      status: contact.status,
    };

    this.validation = this.getInitialValidation();
    this.isDirty = false;
  }

  resetForm() {
    runInAction(() => {
      this.formData = this.getInitialFormData();
      this.validation = this.getInitialValidation();
      this.isDirty = false;
      this.contactId = null;
      this.contact = null;
    });
  }

  private getInitialFormData(): ContactFormData {
    return {
      firstName: "",
      lastName: "",
      jobTitle: "",
      department: "",
      email: "",
      phone: "",
      mobilePhone: "",
      accountId: null,
      mailingStreet: "",
      mailingCity: "",
      mailingState: "",
      mailingPostalCode: "",
      mailingCountry: "",
      linkedInProfile: "",
      isPrimaryContact: false,
      status: "Prospect",
    };
  }

  private getInitialValidation(): ContactFormValidation {
    return {
      firstName: [],
      lastName: [],
      email: [],
      phone: [],
      accountId: [],
      mailingStreet: [],
      mailingCity: [],
      mailingState: [],
      mailingPostalCode: [],
      mailingCountry: [],
      general: [],
    };
  }

  // Computed properties
  get availableAccountsComputed(): Array<{ id: number; name: string }> {
    const customersStore = this.rootStore.customersStore as ServiceStore;
    return customersStore.entities.map((account: unknown) => {
      const accountData = account as {
        accountId: number;
        name?: string;
        accountName?: string;
      };
      return {
        id: accountData.accountId,
        name:
          accountData.name ||
          accountData.accountName ||
          `Account ${accountData.accountId}`,
      };
    });
  }

  get hasValidationErrors(): boolean {
    return Object.values(this.validation).some(
      (errors: string[]) => Array.isArray(errors) && errors.length > 0
    );
  }

  get canSave(): boolean {
    return this.isDirty && !this.isSubmitting && !this.hasValidationErrors;
  }

  get formTitle(): string {
    return this.contactId ? "Edit Contact" : "New Contact";
  }

  get submitButtonText(): string {
    if (this.isSubmitting) {
      return this.contactId ? "Updating..." : "Creating...";
    }
    return this.contactId ? "Update Contact" : "Create Contact";
  }

  get selectedAccountName(): string {
    if (!this.formData.accountId) return "";
    const account = this.availableAccounts.find(
      (acc: Account) => acc.accountId === this.formData.accountId
    );
    return account?.name || "";
  }
}

export default ContactFormStore;
